import {FC, useEffect, useRef} from "react";
import {useSelector, useDispatch} from "react-redux";
import moment from "moment";

import Select from "../../custom/select/select";
import CustomMultiSelect from "../../custom/customMultiSelect/customMultiSelect";
import RangeDatePicker from "../../custom/rangeDatePicker/rangeDatePicker";

import {reducerModalFilters} from "../../../redux/reducer/charts/reducer/reducerCharts";
import {actionAnalyticFilters} from "../../../redux/action/charts/actionCharts";
import {AppDispatch} from "../../../redux/reducer/store";

import {
    defaultFilter,
    periodOptions,
    skuOptions,
} from "../../../utils/chartsData";
import {IUserOptions} from "../../../types/interfaces";

interface IFiltersSectionOptions {
    inputValues?: any;
    id: string | undefined;
    chartId: any;
}

const groupList = [
    "subgroup",
    "group",
    "category",
    "subcategory",
    "sku",
    "pos",
    "chain",
    "action",
];

const groupOptions = [
    {value: "0", label: "По дате"},
    {value: "1", label: "По контракту"},
    {value: "3", label: "По сети"},
    {value: "4", label: "По области"},
    {value: "5", label: "По региону"},
];

const groupActionsOptions = [
    {value: "0", label: "По дате"},
    {value: "4", label: "По контракту"},
    {value: "5", label: "По проекту"},
    {value: "6", label: "По сети"},
    {value: "7", label: "По области"},
    {value: "8", label: "По региону"},
    {value: "9", label: "Акции"},
];

const groupActionsOptionsDate = [
    {value: "0", label: "Группировать по дням"},
    {value: "1", label: "Группировать по неделям"},
    {value: "2", label: "Группировать по месяцам"},
    {value: "3", label: "Группировать по годам"},
];

const FiltersChartSection: FC<IFiltersSectionOptions> = ({
                                                             id,
                                                             chartId,
                                                             inputValues,
                                                         }) => {
    const dispatch: any = useDispatch<AppDispatch>();

    const modalRef = useRef<HTMLDivElement | null>(null);

    const {pos, chain, desktopList, modalFilters, signalFiltersOptions,} = useSelector((state: any) => state.charts?.chartsList);

    const {
        rateOptions,
        areasOptions,
        reviewOptions,
        regionsOptions,
        projectOptions,
        contractsOptions,
    } = useSelector((state: any) => state.userReports.userReports);

    const {data} = useSelector((state: IUserOptions) => state.user.user);
    const contractsLength = data?.settings?.contracts?.length || 0;

    const index = id
                  ? desktopList.findIndex((el: any) => el.id.toString() === id)
                  : 0;

    const desktop = desktopList[index].list;

    const foundItem = desktop.find((chart: any) => chart.id === chartId);
    const skuType =
        foundItem.type === "oss" ||
        foundItem.type === "mediumFace" ||
        foundItem.type === "facing";
    let defaultValueSelect: any;

    if (
        !["0", "7", "8", "9", "13", "12", "14"].includes(modalFilters.group.value)
    ) {
        if (
            modalFilters.group.value === "10" &&
            modalFilters.group.label !== "Статус проверки" &&
            modalFilters.group.value === "10" &&
            modalFilters.group.label !== "Статус оценки"
        ) {
            defaultValueSelect = {value: "12", label: "Товар"};
        } else {
            defaultValueSelect = modalFilters.group;
        }
    } else {
        defaultValueSelect = modalFilters.group;
    }

    const dateValue =
        foundItem.type === "actions"
        ? groupActionsOptionsDate[0]
        : periodOptions[0];

    const additionalOptions = [
        foundItem.type !== "tanderSignal" ? {value: "2", label: "По проекту"} : null,
        foundItem.type === "checkStatus" ? {value: "10", label: "Статус проверки"} : null,
        foundItem.type === "rateStatus" ? {value: "10", label: "Статус оценки"} : null,
        skuType && foundItem.type !== "oss" ? {value: "12", label: "Товар"} : null,
    ];

    const visibleSectionDate =
        modalFilters.group.value === "0" ||
        (foundItem.type !== "actions" &&
         modalFilters.group.value > 6 &&
         modalFilters.group.value < 10) ||
        (foundItem.type === "actions" && modalFilters.group.value < 4);

    const finalGroupOptions = [
        ...groupOptions,
        ...additionalOptions.filter((option) => option !== null),
    ];

    const createDate = {
        formattedDateFrom: moment(modalFilters.fromDate, "YYYY-MM-DD").format(
            "DD/MM/YYYY"
        ),
        formattedDateTo: moment(modalFilters.toDate, "YYYY-MM-DD").format(
            "DD/MM/YYYY"
        ),
    };

    const handleSelect = (event: { value: string; label: string }) => {
        dispatch(reducerModalFilters({...modalFilters, group: event}));
    };

    const handleChangeInput = (event: any) => {
        const {name, value} = event.target;
        dispatch(reducerModalFilters({...modalFilters, [name]: value}));
    };

    const handleClickReset = () => {
        dispatch(reducerModalFilters(defaultFilter));
        if (inputValues.status) {
            dispatch(
                reducerModalFilters({...defaultFilter, status: inputValues.status})
            );
        } else {
            dispatch(reducerModalFilters(defaultFilter));
        }
    };

    const getLabelsList = (values: number[], options: any[]) => {
        return values.map((value) => {
            const finedItem = options.find((option) => option.value === value);
            return finedItem ? finedItem.label : null;
        });
    };

    useEffect(() => {
        dispatch(reducerModalFilters(inputValues));
        // eslint-disable-next-line
    }, [inputValues]);

    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (modalRef.current) {
                const {scrollHeight, clientHeight} = modalRef.current;

                if (scrollHeight > clientHeight) {
                    modalRef.current.scrollTo({
                                                  top: 175,
                                                  behavior: "smooth",
                                              });
                }
            }
        });

        const elem = modalRef.current;

        if (elem) {
            observer.observe(elem, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            observer.disconnect();
        };
    }, [modalRef]);

    useEffect(() => {
        dispatch(actionAnalyticFilters(id || "0", groupList, true));
        // eslint-disable-next-line
    }, []);

    return (
        <div>
            <div className="charts__filters_tools">
                {foundItem.type !== "reportsChart" && (
                    <span className="charts__span">Группировать</span>
                )}
                <button onClick={handleClickReset} className="reset-btn">
                    Сбросить фильтры
                </button>
            </div>
            <div className="charts__filters" ref={modalRef}>
                {foundItem.type !== "reportsChart" && (
                    <Select
                        options={
                            foundItem.type === "actions"
                            ? groupActionsOptions
                            : finalGroupOptions
                        }
                        propsChange={(event: { value: string; label: string }) =>
                            handleSelect(event)
                        }
                        value={
                            visibleSectionDate
                            ? foundItem.type === "actions"
                              ? groupActionsOptions[0]
                              : groupOptions[0]
                            : defaultValueSelect
                        }
                        placeholder={
                            foundItem.type === "actions"
                            ? groupActionsOptions[3]
                            : finalGroupOptions[3]
                        }
                        className="users__filter"
                        name="group"
                        isShowPlaceholder={true}
                        style={{width: "280px", bottom: "-1px"}}
                    />
                )}
                {foundItem.type !== "reportsChart" && (
                    <div>
                        {visibleSectionDate && (
                            <Select
                                className="users__filter"
                                defaultValue={dateValue}
                                value={
                                    modalFilters.group.value === "0"
                                    ? dateValue
                                    : modalFilters.group
                                }
                                propsChange={(event: { value: string; label: string }) =>
                                    handleSelect(event)
                                }
                                options={
                                    foundItem.type === "actions"
                                    ? groupActionsOptionsDate
                                    : periodOptions
                                }
                                placeholder={"Группировать по"}
                                name="period"
                                disabled={false}
                                style={{width: "280px", bottom: "-1px"}}
                                isShowPlaceholder={true}
                            />
                        )}
                        {(modalFilters.group.value === "12" ||
                          (modalFilters.group.value >= 10 &&
                           modalFilters.group.label !== "Статус проверки" &&
                           modalFilters.group.label !== "Статус оценки" &&
                           modalFilters.group.value < 15)) && (
                             <Select
                                 className="users__filter"
                                 defaultValue={skuOptions[3]}
                                 value={modalFilters.group}
                                 propsChange={(event: { value: string; label: string }) =>
                                     handleSelect(event)
                                 }
                                 options={skuOptions}
                                 placeholder={"Группировать по"}
                                 name="period"
                                 disabled={false}
                                 style={{width: "280px", bottom: "-1px"}}
                                 isShowPlaceholder={true}
                             />
                         )}
                    </div>
                )}
                <div className="charts__span_header">
                    {foundItem.type !== "reportsChart" && (
                        <span className="charts__span charts__span_filter">
              Фильтровать
            </span>
                    )}
                </div>
                <div className="charts__select charts__filters_dataPicker">
                    <RangeDatePicker
                        setLoading={() => {
                        }}
                        reducer={reducerModalFilters}
                        defaultDateFrom={createDate.formattedDateFrom}
                        inputValues={modalFilters}
                        defaultDateTo={createDate.formattedDateTo}
                        setCurrentPage={() => {
                        }}
                        action={actionAnalyticFilters}
                        setStartPage={() => {
                        }}
                        limit={10}
                        page={1}
                        placeholder="Период"
                        pageName="actions"
                        name="date"
                        id={id}
                    />
                </div>
                {contractsLength > 1 && (
                    <div className="charts__select charts__select_end multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {}}
                            inputValues={modalFilters}
                            options={foundItem.type !== "tanderSignal" ? contractsOptions : signalFiltersOptions.contract}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Контракт"
                            name="contracts"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {}}
                            activeOptions={modalFilters.contracts}
                            id={chartId}
                            lowerDown={true}
                        />
                        <div className="multiselectContent">
                            {modalFilters.contracts && modalFilters.contracts.length > 0 && (
                                <span className="span__elem">
                  {getLabelsList(modalFilters.contracts, contractsOptions).join(", ")}
                </span>
                            )}
                        </div>
                    </div>
                )}
                {foundItem.type !== "tanderSignal" && (<div className="charts__select multiselect__filter_block">
                    <CustomMultiSelect
                        setLoading={() => {
                        }}
                        inputValues={modalFilters}
                        options={projectOptions || []}
                        isShowPlaceholder={true}
                        pageName="mapTypes1"
                        placeholder="Проект"
                        name="projects"
                        limit={0}
                        reducer={reducerModalFilters}
                        resetPages={() => {
                        }}
                        activeOptions={modalFilters.projects}
                        id={chartId}
                    />
                    {modalFilters.projects && modalFilters.projects.length > 0 && (
                        <span className="span__elem">
              {getLabelsList(modalFilters.projects, projectOptions).join(", ")}
            </span>
                    )}
                </div>)}
                <div className="charts__select multiselect__filter_block">
                    <CustomMultiSelect
                        setLoading={() => {
                        }}
                        inputValues={modalFilters}
                        options={foundItem.type !== "tanderSignal" ? pos : signalFiltersOptions.pos}
                        isShowPlaceholder={true}
                        pageName="mapTypes1"
                        placeholder="Торговая точка"
                        name="posIds"
                        limit={0}
                        reducer={reducerModalFilters}
                        resetPages={() => {
                        }}
                        id={chartId}
                        activeOptions={modalFilters.posIds}
                    />
                    {modalFilters.posIds && modalFilters.posIds.length > 0 && (
                        <span className="span__elem">
              {getLabelsList(modalFilters.posIds, pos).join(", ")}
            </span>
                    )}
                </div>
                <div className="charts__select multiselect__filter_block">
                    <CustomMultiSelect
                        setLoading={() => {
                        }}
                        inputValues={modalFilters}
                        options={foundItem.type !== "tanderSignal" ? chain : signalFiltersOptions.chain}
                        isShowPlaceholder={true}
                        pageName="mapTypes1"
                        placeholder="Сеть"
                        name="retailChains"
                        limit={0}
                        reducer={reducerModalFilters}
                        resetPages={() => {
                        }}
                        activeOptions={modalFilters.retailChains}
                        id={chartId}
                    />
                    {modalFilters.retailChains &&
                     modalFilters.retailChains.length > 0 && (
                         <span className="span__elem">
                {getLabelsList(modalFilters.retailChains, chain).join(", ")}
              </span>
                     )}
                </div>
                <div
                    className="charts__select filter__number_block"
                    style={{alignSelf: "center"}}
                >
                    <input
                        value={modalFilters?.city || ""}
                        name="city"
                        placeholder=" "
                        type="text"
                        onChange={(date) => handleChangeInput(date)}
                        style={{width: "280px"}}
                    />
                    <label style={{top: "40%"}}>Город</label>
                </div>
                <div className="charts__select multiselect__filter_block">
                    <CustomMultiSelect
                        setLoading={() => {
                        }}
                        inputValues={modalFilters}
                        options={foundItem.type !== "tanderSignal" ? areasOptions : signalFiltersOptions.area}
                        isShowPlaceholder={true}
                        pageName="mapTypes1"
                        placeholder="Область"
                        name="areas"
                        limit={0}
                        reducer={reducerModalFilters}
                        resetPages={() => {
                        }}
                        activeOptions={modalFilters.areas}
                        id={chartId}
                    />
                    {modalFilters.areas && modalFilters.areas.length > 0 && (
                        <span className="span__elem">
              {getLabelsList(modalFilters.areas, areasOptions).join(", ")}
            </span>
                    )}
                </div>
                <div className="charts__select multiselect__filter_block">
                    <CustomMultiSelect
                        setLoading={() => {
                        }}
                        inputValues={modalFilters}
                        options={foundItem.type !== "tanderSignal" ? regionsOptions : signalFiltersOptions.region}
                        isShowPlaceholder={true}
                        pageName="mapTypes1"
                        placeholder="Регион"
                        name="regions"
                        limit={0}
                        reducer={reducerModalFilters}
                        resetPages={() => {
                        }}
                        activeOptions={modalFilters.regions}
                        id={chartId}
                    />
                    {modalFilters.regions && modalFilters.regions.length > 0 && (
                        <span className="span__elem">
              {getLabelsList(modalFilters.regions, regionsOptions).join(", ")}
            </span>
                    )}
                </div>
                {skuType && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={modalFilters.groupFilters.category}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Категория товара"
                            name="categorySku"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            id={chartId}
                            activeOptions={modalFilters.categorySku}
                        />
                        {modalFilters.categorySku &&
                         modalFilters.categorySku.length > 0 && (
                             <span className="span__elem">
                  {getLabelsList(
                      modalFilters.categorySku,
                      modalFilters.groupFilters.category
                  ).join(", ")}
                </span>
                         )}
                    </div>
                )}
                {skuType && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={modalFilters.groupFilters.subcategory}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Подкатегория товара"
                            name="subCategorySku"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            id={chartId}
                            activeOptions={modalFilters.subCategorySku}
                        />
                        {modalFilters.subCategorySku &&
                         modalFilters.subCategorySku.length > 0 && (
                             <span className="span__elem">
                  {getLabelsList(
                      modalFilters.subCategorySku,
                      modalFilters.groupFilters.subcategory
                  ).join(", ")}
                </span>
                         )}
                    </div>
                )}
                {skuType && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={modalFilters.groupFilters.group}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Группа товара"
                            name="groupSku"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            id={chartId}
                            activeOptions={modalFilters.groupSku}
                        />
                        {modalFilters.groupSku && modalFilters.groupSku.length > 0 && (
                            <span className="span__elem">
                {getLabelsList(
                    modalFilters.groupSku,
                    modalFilters.groupFilters.group
                ).join(", ")}
              </span>
                        )}
                    </div>
                )}
                {skuType && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={modalFilters.groupFilters.subgroup}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Подруппа товара"
                            name="subGroupSku"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            activeOptions={modalFilters.subGroupSku}
                            id={chartId}
                        />
                        {modalFilters.subGroupSku &&
                         modalFilters.subGroupSku.length > 0 && (
                             <span className="span__elem">
                  {getLabelsList(
                      modalFilters.subGroupSku,
                      modalFilters.groupFilters.subgroup
                  ).join(", ")}
                </span>
                         )}
                    </div>
                )}
                {skuType && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={modalFilters.groupFilters.sku}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Товар"
                            name="skuIds"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            activeOptions={modalFilters.skuIds}
                            id={chartId}
                        />
                        {modalFilters.skuIds && modalFilters.skuIds.length > 0 && (
                            <span className="span__elem">
                {getLabelsList(
                    modalFilters.skuIds,
                    modalFilters.groupFilters.sku
                ).join(", ")}
              </span>
                        )}
                    </div>
                )}
                {foundItem.type === "actions" && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={modalFilters.groupFilters.action}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Акция"
                            name="actionIds"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            activeOptions={modalFilters.actionIds}
                            id={chartId}
                        />
                        {modalFilters.actionIds && modalFilters.actionIds.length > 0 && (
                            <span className="span__elem">
                {getLabelsList(
                    modalFilters.actionIds,
                    modalFilters.groupFilters.action
                ).join(", ")}
              </span>
                        )}
                    </div>
                )}
                {foundItem.type === "rateStatus" && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={rateOptions}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Статус оценки"
                            name="rateStatuses"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            activeOptions={modalFilters.rateStatuses}
                            id={chartId}
                        />
                        {modalFilters.rateStatuses &&
                         modalFilters.rateStatuses.length > 0 && (
                             <span className="span__elem">
                  {getLabelsList(modalFilters.rateStatuses, rateOptions).join(
                      ", "
                  )}
                </span>
                         )}
                    </div>
                )}
                {foundItem.type === "checkStatus" && (
                    <div className="charts__select multiselect__filter_block">
                        <CustomMultiSelect
                            setLoading={() => {
                            }}
                            inputValues={modalFilters}
                            options={reviewOptions}
                            isShowPlaceholder={true}
                            pageName="mapTypes1"
                            placeholder="Статус проверки"
                            name="reviewStatuses"
                            limit={0}
                            reducer={reducerModalFilters}
                            resetPages={() => {
                            }}
                            activeOptions={modalFilters.reviewStatuses}
                            id={chartId}
                        />
                        {modalFilters.reviewStatuses &&
                         modalFilters.reviewStatuses.length > 0 && (
                             <span className="span__elem">
                  {getLabelsList(
                      modalFilters.reviewStatuses,
                      reviewOptions
                  ).join(", ")}
                </span>
                         )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FiltersChartSection;
